import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { catalogueVersion, getLoadedCatalogue, loadCatalogue, subscribeCatalogues } from '../../data/catalogue';
import type { Catalogue, CatalogueKey } from '../../types';

/**
 * Ensures every one of `keys` is loaded (kicking off a fetch for any that
 * isn't cached yet — `loadCatalogue()` itself dedupes concurrent/repeat
 * requests for the same key) and returns whichever are ready so far,
 * re-rendering as more of them arrive. A key still loading (or that failed
 * to load) is simply absent from the returned map — callers treat "not in
 * the map yet" as a loading state, the same way `resolveProgramForType()`
 * treats it as "nothing resolves yet" rather than throwing.
 *
 * `null`/duplicate entries in `keys` are ignored. The array is compared by
 * its joined contents (`CatalogueKey` values are plain strings), not by
 * reference, so passing a freshly built array every render is fine.
 */
export function useCatalogues(keys: ReadonlyArray<CatalogueKey | null>): ReadonlyMap<CatalogueKey, Catalogue> {
  const joined = keys.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `joined` is the intentional, content-based dependency (see doc comment)
  const wanted = useMemo(() => [...new Set(keys.filter((k): k is CatalogueKey => k !== null))], [joined]);

  useEffect(() => {
    for (const key of wanted) {
      if (!getLoadedCatalogue(key)) void loadCatalogue(key).catch(() => {});
    }
  }, [wanted]);

  const version = useSyncExternalStore(subscribeCatalogues, catalogueVersion);

  return useMemo(() => {
    const map = new Map<CatalogueKey, Catalogue>();
    for (const key of wanted) {
      const cat = getLoadedCatalogue(key);
      if (cat) map.set(key, cat);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `version` is the intentional dependency: re-derive whenever the cache notifies, not just when `wanted` changes
  }, [wanted, version]);
}

/** Singular convenience for the common one-catalogue case. */
export function useCatalogue(key: CatalogueKey | null): Catalogue | undefined {
  const keys = useMemo(() => [key], [key]);
  const map = useCatalogues(keys);
  return key === null ? undefined : map.get(key);
}
